/**
 * Unit economics — read from AEOS, never computed here.
 *
 * WHY PEREGRINE DOES NOT CALCULATE THIS
 *
 * There were two implementations of this arithmetic, one here and one in AEOS, and neither was
 * wired to data. Two copies of the same calculation over the same activity is a guarantee that
 * they eventually disagree and nobody can say which is right. So the calculation lives in AEOS,
 * where the activity data is, and this panel shows the answer.
 *
 * WHY THE QUALIFICATIONS ARE AS PROMINENT AS THE NUMBERS
 *
 * AEOS instruments no model token usage, its compute figure is inferred from elapsed sync time
 * rather than measured, and the split by app is a human declaration with known gaps. Any one of
 * those makes a cost-per-unit figure a floor rather than an estimate. The service returns those
 * caveats per row precisely so a client cannot render the money and drop the context.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';

import { AeosCoverageNotice, AeosInvalidItemsAlert } from './AeosEstatePanels';
import { getAeosEcon, type AeosEconReport, type AeosEconRollUpRow } from '../../api/aeos';

const PERIOD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Money at six decimal places.
 *
 * A cost per delivered record is routinely a small fraction of a cent. Rounding to two decimals
 * renders most of these figures as `$0.00`, which reads as free — the single most misleading
 * thing this panel could display.
 */
function formatMoney(value: number, digits = 6): string {
  return `$${value.toFixed(digits)}`;
}

/**
 * The headline figure, or the reason there isn't one.
 *
 * The two reasons differ completely and must not render alike: mixed units means the roll-up
 * refuses to average incomparable work, while no units means nothing was delivered at all.
 */
function formatPerUnit(row: AeosEconRollUpRow): string {
  if (row.costPerUnit !== undefined) {
    return `${formatMoney(row.costPerUnit)}${row.unit === undefined ? '' : ` / ${row.unit}`}`;
  }
  return row.mixedUnits ? 'mixed units — not averaged' : 'no units delivered';
}

function rollUpColumns(scopeLabel: string) {
  return [
    {
      id: 'scope',
      header: scopeLabel,
      cell: (row: AeosEconRollUpRow) => row.scope,
      sortingField: 'scope',
    },
    {
      id: 'runs',
      header: 'Runs',
      cell: (row: AeosEconRollUpRow) =>
        row.failed > 0 ? `${row.invocations} (${row.failed} failed)` : String(row.invocations),
    },
    {
      id: 'units',
      header: 'Units delivered',
      cell: (row: AeosEconRollUpRow) => String(row.units),
    },
    {
      id: 'cost',
      header: 'Cost',
      cell: (row: AeosEconRollUpRow) => formatMoney(row.cost, 4),
    },
    {
      id: 'split',
      header: 'Tokens / compute',
      cell: (row: AeosEconRollUpRow) =>
        `${formatMoney(row.tokenCost, 4)} / ${formatMoney(row.resourceCost, 4)}`,
    },
    {
      id: 'perUnit',
      header: 'Per unit',
      cell: (row: AeosEconRollUpRow) => formatPerUnit(row),
    },
    {
      id: 'qualifications',
      header: 'Qualifications',
      cell: (row: AeosEconRollUpRow) =>
        row.qualifications.length === 0 ? (
          '—'
        ) : (
          <ul>
            {row.qualifications.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        ),
    },
  ];
}

interface AeosEconPanelProps {
  reloadToken: number;
}

export function AeosEconPanel({ reloadToken }: AeosEconPanelProps): ReactElement {
  const [report, setReport] = useState<AeosEconReport | undefined>();
  const [periodInput, setPeriodInput] = useState('');
  const [appliedPeriod, setAppliedPeriod] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let isCancelled = false;
    setIsLoading(true);
    setError(undefined);
    void getAeosEcon(appliedPeriod)
      .then((response) => {
        if (!isCancelled) {
          setReport(response);
        }
      })
      .catch((econError: unknown) => {
        if (!isCancelled) {
          setError(
            econError instanceof Error ? econError.message : 'Unable to read the AEOS economics',
          );
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [appliedPeriod, reloadToken]);

  const applyPeriod = useCallback(() => {
    const trimmed = periodInput.trim();
    // Validated here as well as in the handler so a typo is a message rather than a 400.
    setAppliedPeriod(trimmed.length === 0 ? undefined : trimmed);
  }, [periodInput]);

  const periodInvalid = periodInput.trim().length > 0 && !PERIOD_PATTERN.test(periodInput.trim());

  return (
    <SpaceBetween size="l">
      {error ? (
        <Alert type="error" header="Economics request failed">
          {error}
        </Alert>
      ) : null}

      <Container
        header={
          <Header
            variant="h2"
            description="Computed nightly in AEOS from completed integration runs. Peregrine reads these figures and performs no calculation of its own, so there is no second number here that can disagree with AEOS."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={applyPeriod} disabled={periodInvalid || isLoading}>
                  Load period
                </Button>
              </SpaceBetween>
            }
          >
            Unit economics
          </Header>
        }
      >
        <SpaceBetween size="m">
          <FormField
            label="Period"
            description="A single UTC day. Leave blank for the most recent period AEOS finished computing."
            errorText={periodInvalid ? 'Use YYYY-MM-DD' : undefined}
          >
            <Input
              value={periodInput}
              onChange={(event) => setPeriodInput(event.detail.value)}
              placeholder="YYYY-MM-DD"
              type="text"
            />
          </FormField>

          {isLoading ? (
            <StatusIndicator type="loading">Reading AEOS economics</StatusIndicator>
          ) : report === undefined ? (
            <Box color="text-status-inactive">No economics loaded.</Box>
          ) : !report.computed ? (
            /*
             * The important empty state. "AEOS has never computed this" and "AEOS computed it
             * and it cost nothing" are indistinguishable in a table of zeros, so say which.
             */
            <Alert type="info" header="No figures computed">
              AEOS has not computed unit economics
              {report.period === null ? ' for any period yet' : ` for ${report.period}`}. This is
              an absence of figures, not a cost of zero. The AEOS roll-up runs daily at 07:00 UTC
              and can be triggered from the AEOS console.
            </Alert>
          ) : (
            <SpaceBetween size="m">
              <AeosInvalidItemsAlert
                invalidItemCount={report.invalidItemCount}
                subject="unit-economics rows"
              />
              <AeosCoverageNotice coverage={report.coverage} surfaceLabel="economics" />

              <ColumnLayout columns={4} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Period</Box>
                  <div>{report.period}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Total cost</Box>
                  <div>{report.total === null ? '—' : formatMoney(report.total.cost, 4)}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Units delivered</Box>
                  <div>{report.total === null ? '—' : String(report.total.units)}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">App declaration revision</Box>
                  {/*
                    Which AEOS mapping produced the split. Without it, a period recomputed after
                    an attribution fix looks like the cost moved rather than the attribution.
                  */}
                  <div>{report.attributionRevision ?? 'unversioned'}</div>
                </div>
              </ColumnLayout>

              <Table
                columnDefinitions={rollUpColumns('App')}
                items={report.byApp}
                loading={false}
                variant="embedded"
                header={<Header variant="h3">By app</Header>}
                empty={
                  <Box color="text-status-inactive">
                    No app rows. Every integration resolved to unattributed, which means the AEOS
                    app declaration does not cover any of the activity in this period.
                  </Box>
                }
              />

              <Table
                columnDefinitions={rollUpColumns('Integration')}
                items={report.byWorkflow}
                loading={false}
                variant="embedded"
                header={<Header variant="h3">By integration</Header>}
                empty={<Box color="text-status-inactive">No integration rows.</Box>}
              />
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}
